import React, {
    FC,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { Link } from 'react-router-dom';
import cn from 'classnames';
import styles from './navigationMenu.module.scss';
import Triangle from '../../ui/icons/triangle';

type MenuItem = {
    id: string;
    label: string;
    route?: string;
    children?: MenuItem[];
    external?: boolean;
};

type NavigationMenuProps = {
    items: MenuItem[];
    activeId?: string;
    onSelect?: (item: MenuItem) => void;
    variant?: 'default' | 'muted';
    className?: string;
    dropdownPlacement?: 'down' | 'up';
    onOverlayToggle?: (visible: boolean) => void;
};

const CLOSE_DELAY = 130;

export const NavigationMenu: FC<NavigationMenuProps> = ({
    items,
    activeId,
    onSelect,
    variant = 'default',
    className,
    dropdownPlacement = 'down',
    onOverlayToggle,
}) => {
    const [openId, setOpenId] = useState<string | null>(null);
    const closeTimer = useRef<number | null>(null);
    const menuRef = useRef<HTMLDivElement | null>(null);
    const triggerRefs = useRef<Record<string, HTMLElement | null>>({});
    const menuMetricsRef = useRef<{ left: number; width: number } | null>(null);
    const [dropdownPosition, setDropdownPosition] = useState({
        top: 0,
        panelLeft: 0,
        panelWidth: 0,
        columnOffset: 0,
        columnWidth: 0,
    });

    const safeItems = items || [];

    const overlayEnabled = dropdownPlacement === 'down';

    const updateDropdownPosition = useCallback(
        (targetId?: string, options?: { force?: boolean }) => {
            if (!overlayEnabled) return;
            if (typeof window === 'undefined') return;
            const menuElement = menuRef.current;
            if (!menuElement) return;

            const currentId = targetId ?? openId ?? undefined;
            const triggerRect = currentId
                ? triggerRefs.current[currentId]?.getBoundingClientRect() ??
                  null
                : null;

            const menuRect = menuElement.getBoundingClientRect();
            const referenceRect = triggerRect ?? menuRect;
            const docEl =
                typeof document !== 'undefined'
                    ? document.documentElement
                    : null;
            const viewportWidth =
                window.innerWidth || docEl?.clientWidth || menuRect.right;

            const measuredLeft = Math.max(0, menuRect.left);
            const measuredWidth = Math.max(0, menuRect.width);

            const metrics = menuMetricsRef.current;
            const forceUpdate = options?.force ?? false;
            const shouldUpdateMetrics = forceUpdate || !metrics;

            if (shouldUpdateMetrics) {
                menuMetricsRef.current = {
                    left: measuredLeft,
                    width: measuredWidth,
                };
            }

            const { left, width } = menuMetricsRef.current ?? {
                left: measuredLeft,
                width: measuredWidth,
            };

            const panelLeft = 0;
            const panelWidth = Math.max(0, viewportWidth);

            const columnOffset = Math.round(left);
            const columnWidth = Math.round(width);

            setDropdownPosition({
                top: referenceRect.bottom,
                panelLeft,
                panelWidth,
                columnOffset,
                columnWidth,
            });
        },
        [overlayEnabled, openId]
    );

    const cancelCloseTimer = useCallback(() => {
        if (closeTimer.current) {
            window.clearTimeout(closeTimer.current);
            closeTimer.current = null;
        }
    }, []);

    const open = useCallback(
        (id: string) => {
            cancelCloseTimer();
            if (overlayEnabled) {
                updateDropdownPosition(id);
            }
            setOpenId(id);
        },
        [cancelCloseTimer, overlayEnabled, updateDropdownPosition]
    );

    const close = useCallback(() => {
        cancelCloseTimer();
        setOpenId(null);
        menuMetricsRef.current = null;
    }, [cancelCloseTimer]);

    const scheduleClose = useCallback(() => {
        cancelCloseTimer();

        if (overlayEnabled) return;

        closeTimer.current = window.setTimeout(() => {
            setOpenId(null);
            closeTimer.current = null;
        }, CLOSE_DELAY);
    }, [cancelCloseTimer, overlayEnabled]);

    const openItem = useMemo(
        () => safeItems.find(item => item.id === openId) ?? null,
        [safeItems, openId]
    );

    const hasActiveDropdown = Boolean(openItem?.children?.length);

    useEffect(() => {
        if (!hasActiveDropdown) return;
        updateDropdownPosition();

        const handleResize = () =>
            updateDropdownPosition(undefined, { force: true });
        const handleScroll = () =>
            updateDropdownPosition(undefined, { force: true });

        window.addEventListener('resize', handleResize);
        window.addEventListener('scroll', handleScroll, true);

        return () => {
            window.removeEventListener('resize', handleResize);
            window.removeEventListener('scroll', handleScroll, true);
        };
    }, [hasActiveDropdown, updateDropdownPosition]);

    useEffect(() => {
        onOverlayToggle?.(overlayEnabled && hasActiveDropdown);
    }, [hasActiveDropdown, onOverlayToggle, overlayEnabled]);

    useEffect(() => {
        return () => {
            cancelCloseTimer();
            onOverlayToggle?.(false);
        };
    }, [cancelCloseTimer, onOverlayToggle]);

    useEffect(() => {
        if (!hasActiveDropdown) return;

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                close();
            }
        };

        window.addEventListener('keydown', handleKeyDown);

        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [close, hasActiveDropdown]);

    // Do not lock body scroll; locking was causing layout shift when dropdown opens
    useEffect(() => {
        return undefined;
    }, []);

    return (
        <nav
            ref={menuRef}
            className={cn(
                styles.menu,
                styles[variant],
                className,
                dropdownPlacement === 'up' && styles.dropUp
            )}
        >
            {safeItems.map(item => {
                const isActive = item.id === activeId;
                const isOpen = openId === item.id;
                const hasChildren = !!item.children?.length;

                const handleClick = () => {
                    if (!hasChildren) {
                        onSelect?.(item);
                        return;
                    }

                    if (isOpen) {
                        close();
                    } else {
                        open(item.id);
                    }
                };

                return (
                    <div key={item.id} className={styles.menuItem}>
                        {hasChildren ? (
                            <button
                                type="button"
                                className={cn(styles.triggerButton, {
                                    [styles.active]: isActive,
                                })}
                                aria-expanded={isOpen}
                                aria-haspopup
                                onClick={handleClick}
                                onMouseEnter={() => open(item.id)}
                                onMouseLeave={() => {
                                    if (!overlayEnabled) scheduleClose();
                                }}
                                onFocus={() => open(item.id)}
                                ref={node => {
                                    if (node) {
                                        triggerRefs.current[item.id] = node;
                                    } else {
                                        delete triggerRefs.current[item.id];
                                    }
                                }}
                            >
                                <span className={styles.subMenuLabel}>
                                    {item.label}
                                </span>
                                <span
                                    className={styles.caret}
                                    data-rotated={isOpen && hasChildren}
                                >
                                    <Triangle />
                                </span>
                            </button>
                        ) : item.route ? (
                            <Link
                                to={item.route}
                                className={cn(styles.link, {
                                    [styles.active]: isActive,
                                })}
                                onClick={() => onSelect?.(item)}
                                onMouseEnter={() => {
                                    if (overlayEnabled) {
                                        close();
                                    }
                                }}
                            >
                                {item.label}
                            </Link>
                        ) : (
                            <span
                                className={styles.link}
                                onMouseEnter={() => {
                                    if (overlayEnabled) {
                                        close();
                                    }
                                }}
                            >
                                {item.label}
                            </span>
                        )}

                        {!overlayEnabled && isOpen && hasChildren && (
                            <ul
                                className={styles.subMenu}
                                onMouseEnter={() => open(item.id)}
                                onMouseLeave={scheduleClose}
                            >
                                {item.children!.map(child => (
                                    <li
                                        key={child.id}
                                        className={styles.menuChildrenItem}
                                    >
                                        {child.route &&
                                            (child.external ? (
                                                <a
                                                    href={child.route}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className={styles.link}
                                                >
                                                    {child.label}
                                                </a>
                                            ) : (
                                                <Link
                                                    to={child.route}
                                                    className={styles.link}
                                                >
                                                    {child.label}
                                                </Link>
                                            ))}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                );
            })}

            {overlayEnabled && hasActiveDropdown && openItem && (
                <>
                    <div
                        className={cn(styles.overlayBackdrop, {
                            [styles.visible]: hasActiveDropdown,
                        })}
                        onClick={close}
                        aria-hidden
                        style={{ top: dropdownPosition.top }}
                    />
                    <div
                        className={styles.overlayPanel}
                        style={{
                            top: dropdownPosition.top,
                            left: dropdownPosition.panelLeft,
                            width:
                                dropdownPosition.panelWidth > 0
                                    ? dropdownPosition.panelWidth
                                    : undefined,
                        }}
                        onMouseEnter={() => open(openItem.id)}
                        onMouseLeave={close}
                    >
                        <div
                            className={styles.overlayColumn}
                            style={{
                                marginLeft: dropdownPosition.columnOffset,
                                width:
                                    dropdownPosition.columnWidth > 0
                                        ? dropdownPosition.columnWidth
                                        : undefined,
                            }}
                        >
                            <div className={styles.overlayDivider} />
                            <ul className={styles.overlayList}>
                                {openItem.children!.map(child => (
                                    <li
                                        key={child.id}
                                        className={styles.overlayListItem}
                                    >
                                        {child.route ? (
                                            child.external ? (
                                                <a
                                                    href={child.route}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className={
                                                        styles.overlayLink
                                                    }
                                                    onClick={() => {
                                                        close();
                                                        onSelect?.(child);
                                                    }}
                                                >
                                                    {child.label}
                                                </a>
                                            ) : (
                                                <Link
                                                    to={child.route}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className={
                                                        styles.overlayLink
                                                    }
                                                    onClick={() => {
                                                        close();
                                                        onSelect?.(child);
                                                    }}
                                                >
                                                    {child.label}
                                                </Link>
                                            )
                                        ) : (
                                            <span
                                                className={styles.overlayText}
                                            >
                                                {child.label}
                                            </span>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </>
            )}
        </nav>
    );
};
